import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FeedIssueRecord, IsoDate, MaterialRecord, MaterialType, NumericString } from '@henlytics/types'
import { api } from '../lib/api-client'
import { queryKeys } from '../lib/query-keys'
import { clientUuid } from '../lib/uuid'

export function useMaterials() {
  return useQuery({
    queryKey: queryKeys.materials,
    queryFn: ({ signal }) => api.get<MaterialRecord[]>('/materials', signal),
  })
}

export function useCreateMaterial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; materialType: MaterialType; uom: string }) =>
      api.post<MaterialRecord>('/materials', input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.materials }),
  })
}

export function useFeedIssues(batchId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.feedIssues(batchId ?? ''),
    queryFn: ({ signal }) => api.get<FeedIssueRecord[]>(`/feed-issues?batchId=${batchId}`, signal),
    enabled: Boolean(batchId),
  })
}

export interface NewFeedIssue {
  batchId: string
  materialId: string
  issueDate: IsoDate
  qty: NumericString
  clientUuid?: string
  movementClientUuid?: string
}

export function useCreateFeedIssue(batchId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: NewFeedIssue) =>
      api.post<FeedIssueRecord>('/feed-issues', {
        ...input,
        clientUuid: input.clientUuid ?? clientUuid(),
        // The issue and the stock movement it causes are two ledger rows.
        movementClientUuid: input.movementClientUuid ?? clientUuid(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.feedIssues(batchId) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports })
    },
  })
}
